/**
 * MediaChatBubble Component
 * Floating chat assistant for Media Intelligence Dashboard
 *
 * Phase 2 improvements (YDMA-178):
 * - §2.2: Streaming responses with typing indicator
 * - §2.3: Live data sync via getContextData callback
 * - §2.4: GFM markdown tables, copy/regenerate buttons
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Sparkles,
  Maximize2,
  Minimize2,
  FileCode,
  FileDown,
  RotateCcw,
  Copy,
  Check,
  RefreshCw,
} from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getReportTemplateOptions } from '../config/reportTemplates';
import { logError } from '../services/logger';
import { CreditChip } from './CreditChip';
import { useCreditBalance } from '@/hooks/useCreditBalance';
import {
  generateConversationSummary,
  saveConversationSummary,
} from '../services/chatMemory';
import {
  GUIDED_QUESTIONS,
  sendChatMessageStream,
  extractPageContext,
  extractMMMContext,
  ChatMessage,
  ChatMediaData,
  ChatContext,
  GuidedQuestion,
  ChatMode,
  MMMChatContextData,
} from '../services/mediaChatService';
import { isLookerShaped } from '../config/chatModes';
import ChatChart from './chat/ChatChart';
import type { InsightChatContext } from '@/services/insights/insightChatContext';
import type { LookerChatContext } from '../services/looker/lookerChatContext';
import TypingIndicator from './chat/TypingIndicator';
import {
  HISTORY_TURNS,
  loadMessages,
  saveMessages,
  clearMessages,
} from './chat/chatHistory';

export interface MediaChatBubbleProps {
  /** Which prompt + question set to use. Defaults to 'performance'. */
  mode?: ChatMode;
  /** Performance-mode data. Required when mode === 'performance'. */
  data?: ChatMediaData;
  /** MMM-mode data. Required when mode === 'mmm'. */
  mmmData?: MMMChatContextData;
  /**
   * Insights-mode context. Required when mode === 'insights'.
   *
   * Already built by the page from what it renders — the bubble does not
   * assemble it, so what the model reads and what the user sees cannot drift.
   */
  insightContext?: InsightChatContext;
  /**
   * The tiles' context, for every looker-shaped mode (LOOKER_SHAPED_MODES), built by the
   * page from what it draws. Without it (and without getContextData) such a mode renders nothing.
   */
  lookerContext?: LookerChatContext;
  selectedChannelId?: string | null;
  /** App-level period from PeriodSelector ("week" | "month" | "quarter" | "all"). */
  selectedPeriod?: string;
  onSectionClick?: (sectionId: string) => void;
  /**
   * Separates this page's saved transcript from another page's in the same
   * mode. Two pages share a mode but not a context, and a follow-up answered
   * against the wrong page's data is the failure this prevents. Omit to keep a
   * page's existing history.
   */
  scope?: string;
  /** Override the default question list. */
  guidedQuestions?: GuidedQuestion[];
  /** Override header copy per page. */
  title?: string;
  subtitle?: string;
  welcomeTitle?: string;
  welcomeSubtitle?: string;
  inputPlaceholder?: string;
  /** Optional callback to generate a report from current context (shown after AI responses in MMM mode). */
  onGenerateReport?: (templateKey?: string, chatMessages?: ChatMessage[]) => Promise<void>;
  /** Whether a report is currently being generated. */
  generatingReport?: boolean;
  /**
   * §2.3 Live data sync: when provided, this callback is invoked on each
   * send to get the freshest dashboard data (avoids stale props).
   * Falls back to extracting from props (data/mmmData) if not provided.
   */
  getContextData?: () => ChatContext;
  /**
   * The page has no new data yet: its reads are loading, or it refuses the period. Keep the
   * conversation and any answer in flight, and take no new question until the context arrives.
   */
  contextPending?: boolean;
}

/**
 * Full screen exists to give answers room: an assistant bubble takes most of
 * the card there (a wide table still scrolls inside it), while the user's own
 * short prompts keep a readable column. min-w-0 lets the bubble shrink below
 * its content's width so the table's scroll box, not the bubble, absorbs it.
 */
const bubbleWidth = (expanded: boolean, role: string): string =>
  expanded ? (role === 'user' ? 'max-w-2xl min-w-0' : 'max-w-[90%] min-w-0') : 'max-w-[85%] min-w-0';

/**
 * A table answer can have more columns than the bubble has room for (a full
 * metric breakdown is 12+). Each table gets its own horizontal scroll box, so a
 * wide one scrolls inside the bubble instead of pushing past the card.
 */
const MARKDOWN_COMPONENTS: Components = {
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto max-w-full">
      <table {...props} />
    </div>
  ),
};

const MediaChatBubble: React.FC<MediaChatBubbleProps> = ({
  mode = 'performance',
  data,
  mmmData,
  insightContext,
  lookerContext,
  selectedChannelId = null,
  selectedPeriod = 'month',
  onSectionClick,
  scope,
  guidedQuestions = GUIDED_QUESTIONS,
  title: titleProp,
  subtitle: subtitleProp,
  welcomeTitle: welcomeTitleProp,
  welcomeSubtitle: welcomeSubtitleProp,
  inputPlaceholder: inputPlaceholderProp,
  onGenerateReport,
  generatingReport = false,
  getContextData,
  contextPending = false,
}) => {
  const { t } = useTranslation();
  const title = titleProp ?? t('chat.defaultTitle');
  const subtitle = subtitleProp ?? t('chat.defaultSubtitle');
  const welcomeTitle = welcomeTitleProp ?? t('chat.defaultWelcomeTitle');
  const welcomeSubtitle = welcomeSubtitleProp ?? t('chat.defaultWelcomeSubtitle');
  const inputPlaceholder = inputPlaceholderProp ?? t('chat.defaultInputPlaceholder');
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessages(mode, scope));
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // AI credits (advisory): the shared hook, so this header and the MMM hero
  // can never disagree about the same number. Refreshed after each turn —
  // a turn spends 2-3 upstream calls.
  const { balance: creditBalance, rate: creditRate, refresh: refreshCredits } = useCreditBalance();
  useEffect(() => {
    if (isOpen) void refreshCredits();
  }, [isOpen, refreshCredits]);

  const handleClearChat = async (): Promise<void> => {
    // §3.2 Generate + save conversation summary before clearing.
    //
    // This calls Gemini, which COSTS A CREDIT. On a control labelled "clear",
    // that is a surprise, so the label now says so and the balance is
    // re-read afterwards — the chip used to keep showing the pre-clear number
    // until something else happened to refresh it.
    if (messages.length > 0) {
      try {
        const summary = await generateConversationSummary(mode, messages);
        if (summary) {
          saveConversationSummary(mode, summary, scope);
        }
      } catch {
        // non-critical — clear proceeds regardless
      } finally {
        void refreshCredits();
      }
    }
    clearMessages(mode, scope);
    setMessages([]);
  };

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    saveMessages(mode, messages, scope);
  }, [messages, mode]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreaming) scrollToBottom();
  }, [streamingText, isStreaming]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      inputRef.current?.focus();
    }
  }, [isOpen, messages.length]);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * §2.3 Gets fresh dashboard context — uses getContextData callback if
   * provided (live sync), otherwise falls back to extracting from props.
   */
  const getCurrentContext = useCallback((): ChatContext => {
    if (getContextData) {
      return getContextData();
    }
    if (mode === 'mmm') return extractMMMContext(mmmData ?? {});
    // The insights page hands its context down whole; there is nothing to
    // extract here, and a fabricated empty one would be a lie about the page.
    if (mode === 'insights' && insightContext) return insightContext;
    if (isLookerShaped(mode) && lookerContext) return lookerContext;
    return extractPageContext(data ?? { channels: [] }, selectedChannelId, selectedPeriod);
  }, [getContextData, mode, mmmData, insightContext, lookerContext, data, selectedChannelId, selectedPeriod]);

  // Note: AI-generated welcome questions removed (YDMA-205 §4 feedback).
  // Initial fetch was slow (~3-5s Gemini call) and pulled dashboard context
  // before the user had picked an analysis direction. Now we use static
  // broad framing questions from the agent profile — user picks direction first.

  /**
   * Ask `question`, with `priorTurns` as the conversation that came before it.
   *
   * Both callers pass the history explicitly rather than letting this read
   * `messages`: the state has not committed yet at this point, so the closure
   * still holds the value from before the turn was added. Sending a question is
   * unaffected by that (the prior turns ARE the pre-append value), but
   * regenerating is not — it removes the answer first, and the stale closure
   * handed that answer straight back to the model.
   */
  const sendTurn = async (question: string, priorTurns: ChatMessage[]): Promise<void> => {
    setIsLoading(true);
    setIsStreaming(false);
    setStreamingText('');

    const pageContext = getCurrentContext();

    try {
      // §2.2 Use streaming path — shows typing indicator then incremental text
      setIsStreaming(true);

      const response = await sendChatMessageStream(
        question,
        pageContext,
        // Matches what loadMessages() keeps on reload. Uncapped, a long session
        // grew the prompt by ~50 tokens a message with the oldest turns — whose
        // numbers belong to an earlier window — still arguing with the current
        // context.
        priorTurns.slice(-HISTORY_TURNS),
        mode,
        (partialText) => {
          setStreamingText(partialText);
        },
        // Without this the visible transcript is this page's while the
        // summarised memory injected into the prompt is the other page's.
        scope,
        // The same handler that decides whether a link is rendered decides
        // whether one is offered. A page with no handler cannot navigate, so
        // the model is told not to name sections at all rather than handed
        // /campaigns' list — /insights and the dashboard both mount without one.
        Boolean(onSectionClick),
      );

      setIsStreaming(false);
      setStreamingText('');

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        text: response.text || t("chat.processing"),
        links: response.links || [],
        insight: response.insight || null,
        followUpQuestions: response.followUpQuestions || [],
        chart: response.chart ?? null,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Chat-to-report trigger: fire onGenerateReport if the AI signaled
      if (response.reportTrigger && onGenerateReport) {
        onGenerateReport(undefined, [
          ...priorTurns,
          { role: 'user', content: question },
          assistantMessage,
        ]);
      }
    } catch (error) {
      logError('MediaChatBubble', 'Chat error', error);
      setIsStreaming(false);
      setStreamingText('');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: t("chat.connectionTrouble"),
          links: [],
          insight: null,
        },
      ]);
    } finally {
      setIsLoading(false);
      // Debounced by construction: once per completed turn, never per chunk.
      void refreshCredits();
    }
  };

  const handleSendMessage = async (text: string = inputValue): Promise<void> => {
    if (!text.trim() || isLoading || contextPending) return;

    const priorTurns = messages;
    setMessages([...priorTurns, { role: 'user', content: text }]);
    setInputValue('');
    await sendTurn(text, priorTurns);
  };

  /**
   * §2.4 Regenerate the last AI response using the same user question.
   */
  const handleRegenerate = (): void => {
    if (isLoading || contextPending) return;

    // The question comes out of the transcript, not out of a second copy held
    // in state. That copy was only ever written while sending, so on a
    // conversation restored from storage it was empty and this returned
    // immediately — a rendered, clickable button that did nothing.
    const withoutLastAnswer =
      messages.length > 0 && messages[messages.length - 1].role === 'assistant'
        ? messages.slice(0, -1)
        : messages;

    const question = withoutLastAnswer[withoutLastAnswer.length - 1];
    if (!question || question.role !== 'user' || !question.content?.trim()) return;

    // Everything before the question is the history; the question is asked
    // again in its original place rather than appended a second time.
    const priorTurns = withoutLastAnswer.slice(0, -1);
    setMessages(withoutLastAnswer);
    void sendTurn(question.content, priorTurns);
  };

  /**
   * §2.4 Copy message text to clipboard.
   */
  const handleCopy = async (text: string, idx: number): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch {
      // clipboard not available
    }
  };

  const handleQuestionClick = (question: GuidedQuestion): void => {
    handleSendMessage(question.text);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderMarkdown = (text: string | undefined): React.ReactNode => {
    if (!text || typeof text !== 'string') {
      return <span className="text-gray-600 italic">{t('chat.noResponse')}</span>;
    }
    return (
      <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:mb-1 prose-headings:mt-2 prose-strong:text-inherit prose-code:text-ink-900 prose-code:bg-primary-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-table:text-xs prose-th:bg-gray-50 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-th:border prose-td:border prose-border-gray-200">
        {/* §2.4: remark-gfm enables tables, strikethrough, task lists */}
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{text}</ReactMarkdown>
      </div>
    );
  };

  // Spec 2026-10-20 page skills §7: a looker-shaped chat reads its page's tiles and nothing else.
  // Without them it would fall through to the performance extractor and send an empty context
  // without an error, so it draws nothing. The pages mount it only once their context exists.
  if (isLookerShaped(mode) && !lookerContext && !getContextData) return null;

  return (
    <>
      {/* Floating Bubble Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-primary-600 hover:bg-primary-700 text-ink-950 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 animate-pulse-slow z-50 group"
          title={mode === 'mmm' ? t('chat.bubbleMmm') : t('chat.bubbleDefault')}
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
            💬
          </span>
          {/* Tooltip */}
          <span className="absolute right-full mr-3 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            {t('chat.bubbleTooltip')}
          </span>
        </button>
      )}

      {/* Chat Box */}
      {isOpen && (
        <div
          className={`fixed bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col z-50 animate-slide-up transition-all duration-300 ${
            isExpanded
              ? 'inset-4 w-auto h-auto max-w-none max-h-none'
              : 'bottom-6 right-6 w-96 h-[520px]'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-indigo-50 rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-sm">
                <Sparkles className="w-5 h-5 text-ink-900" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                  {title}
                  <span className="px-1.5 py-0.5 bg-primary-100 text-ink-900 text-[10px] font-medium rounded-full">
                    AI
                  </span>
                </h3>
                <p className="text-xs text-gray-600 flex items-center gap-1.5">
                  {subtitle}
                  <CreditChip balance={creditBalance} rate={creditRate} />
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              {onGenerateReport && messages.length >= 3 && !isLoading && (
                <button
                  onClick={() => onGenerateReport('chat_export', messages)}
                  disabled={generatingReport}
                  className="px-2 py-1 hover:bg-white hover:shadow-sm rounded-md transition-all flex items-center gap-1 text-xs font-medium text-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  title={t('chat.exportTitle')}
                >
                  {generatingReport ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FileDown className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">{t('chat.exportShort')}</span>
                </button>
              )}
              {messages.length > 0 && !isLoading && (
                <button
                  onClick={handleClearChat}
                  className="p-1.5 hover:bg-white hover:shadow-sm rounded-full transition-all"
                  // The price is named only when the RATE is known, not merely
                  // when the balance is. They are fetched independently, so
                  // `creditRate ?? 1` told a workspace on a rate of 3 that the
                  // clear costs 1. CreditChip already refuses this exact
                  // fallback: an assumed rate is a wrong number wearing a
                  // confident label. The warning still stands without it.
                  title={
                    typeof creditRate === "number"
                      ? t('chat.clearWithCost', { credits: creditRate.toLocaleString() })
                      : creditBalance !== null
                        ? t('chat.clearWithCostUnknown')
                        : t('chat.clearPlain')
                  }
                >
                  <RotateCcw className="w-4 h-4 text-gray-600" />
                </button>
              )}
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded-full transition-all"
                title={isExpanded ? t('chat.shrink') : t('chat.expand')}
              >
                {isExpanded ? (
                  <Minimize2 className="w-4 h-4 text-gray-600" />
                ) : (
                  <Maximize2 className="w-4 h-4 text-gray-600" />
                )}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded-full transition-all"
                title={t('chat.close')}
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
            {/* Welcome Message */}
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-7 h-7 text-ink-900" />
                </div>
                <p className="text-sm text-gray-700 mb-1 font-medium">{welcomeTitle}</p>
                <p className="text-xs text-gray-600 mb-4">{welcomeSubtitle}</p>
                <div className="inline-flex items-center px-3 py-1.5 bg-primary-50 text-ink-900 text-xs rounded-full">
                  {t('chat.pickToStart')}
                </div>
              </div>
            )}

            {/* Guided Questions — broad framing set from agent profile.
                User picks an analysis direction first; agent then drills in. */}
            {messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-600 mb-2">{t('chat.pickDirection')}</p>
                {guidedQuestions.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => handleQuestionClick(q)}
                    disabled={contextPending}
                    className="w-full text-left p-3 bg-white hover:bg-primary-50 border border-gray-200 hover:border-primary-300 rounded-lg transition-all group shadow-sm hover:shadow disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-start space-x-2">
                      <span className="text-lg mt-0.5">{q.emoji}</span>
                      <span className="text-sm text-gray-700 group-hover:text-ink-900 flex-1">
                        {q.text}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Message List */}
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`${bubbleWidth(isExpanded, msg.role)} rounded-2xl px-4 py-3 shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-primary-600 to-primary-700 text-ink-950 rounded-br-sm'
                      : 'bg-white text-gray-900 rounded-bl-sm border border-gray-200'
                  }`}
                >
                  <div className="text-sm leading-relaxed">
                    {renderMarkdown(msg.text || msg.content)}
                  </div>

                  {/* §3.1 Inline Chart — re-enabled via deterministic chatChartMatcher (YDMA-225) */}
                  {msg.role === 'assistant' && msg.chart && <ChatChart spec={msg.chart} />}

                  {/* Action Links — rendered ONLY when the page can act on
                      them. Without a handler the button still drew as a
                      prominent red CTA, closed the chat and did nothing;
                      /insights mounts the bubble without one, so it has been
                      shipping exactly that. A link that cannot go anywhere is
                      worse than no link. */}
                  {onSectionClick && msg.links && msg.links.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {msg.links.map((link, linkIdx) => (
                        <button
                          key={linkIdx}
                          onClick={() => {
                            onSectionClick?.(link.sectionId);
                            setIsOpen(false);
                          }}
                          className="flex items-center w-full text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors font-medium"
                        >
                          <span className="flex-1 text-left">{link.label}</span>
                          <span>→</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Insight Box */}
                  {msg.insight && (
                    <div className="mt-3 p-2.5 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-800 flex items-start gap-1.5">
                        <span>💡</span>
                        <span>{msg.insight}</span>
                      </p>
                    </div>
                  )}

                  {/* Follow-up Questions */}
                  {msg.followUpQuestions && msg.followUpQuestions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-600 mb-2">{t('chat.followUps')}</p>
                      <div className="space-y-1.5">
                        {msg.followUpQuestions.map((fq, fqIdx) => (
                          <button
                            key={fqIdx}
                            onClick={() => handleSendMessage(fq.text)}
                            disabled={contextPending}
                            className="w-full text-left p-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 hover:border-amber-300 rounded-lg transition-all text-xs text-amber-900 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <span className="mr-1.5">{fq.emoji}</span>
                            <span>{fq.text}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* §2.4 Message Action Buttons (Copy + Regenerate) — assistant messages only */}
                  {msg.role === 'assistant' && idx === messages.length - 1 && !isLoading && (
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => handleCopy(msg.text || msg.content || '', idx)}
                        className="flex items-center gap-1 text-[10px] text-gray-600 hover:text-gray-600 transition-colors"
                        title={t('chat.copyResponse')}
                      >
                        {copiedIdx === idx ? (
                          <>
                            <Check className="w-3 h-3" />
                            <span>{t('chat.copied')}</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>{t('chat.copy')}</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleRegenerate}
                        disabled={contextPending}
                        className="flex items-center gap-1 text-[10px] text-gray-600 hover:text-gray-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        title={t('chat.regenerateTitle')}
                      >
                        <RefreshCw className="w-3 h-3" />
                        <span>{t('chat.regenerate')}</span>
                      </button>
                    </div>
                  )}

                  {/* §3.3 Report Template Picker + Create Button — shown after last AI response in MMM mode */}
                  {onGenerateReport && msg.role === 'assistant' && idx === messages.length - 1 && !isLoading && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      {showTemplatePicker ? (
                        <div className="space-y-1.5">
                          <p className="text-xs text-gray-600 mb-1">{t('chat.pickReportType')}</p>
                          {/* Reports exist for performance and mmm only. No page that
                              chats in another mode passes onGenerateReport, so this
                              branch is unreachable there. */}
                          {getReportTemplateOptions(mode === "performance" || mode === "mmm" ? mode : undefined).map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => {
                                setShowTemplatePicker(false);
                                onGenerateReport(opt.value, messages);
                              }}
                              disabled={generatingReport}
                              className="w-full text-left p-2 bg-white hover:bg-emerald-50 border border-gray-200 hover:border-emerald-300 rounded-lg transition-all text-xs disabled:opacity-60"
                            >
                              <span className="font-medium text-gray-800">{opt.labelTh}</span>
                              <span className="block text-[10px] text-gray-600 mt-0.5">{t(opt.descriptionKey)}</span>
                            </button>
                          ))}
                          <button
                            onClick={() => setShowTemplatePicker(false)}
                            className="w-full text-center text-[10px] text-gray-600 hover:text-gray-600 pt-1"
                          >
                            {t('chat.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowTemplatePicker(true)}
                          disabled={generatingReport}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg transition-colors text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {generatingReport ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>{t('chat.generatingReport')}</span>
                            </>
                          ) : (
                            <>
                              <FileCode className="w-3.5 h-3.5" />
                              <span>{t('chat.generateReport')}</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* §2.2 Streaming display: show incremental text with typing indicator */}
            {isStreaming && !streamingText && <TypingIndicator />}

            {isStreaming && streamingText && (
              <div className="flex justify-start">
                <div className={`${bubbleWidth(isExpanded, 'assistant')} bg-white text-gray-900 rounded-2xl rounded-bl-sm border border-gray-200 px-4 py-3 shadow-sm`}>
                  <div className="text-sm leading-relaxed">
                    {renderMarkdown(streamingText)}
                  </div>
                  {/* Streaming cursor */}
                  <span className="inline-block w-1.5 h-4 bg-primary-500 animate-pulse ml-0.5 align-middle" />
                </div>
              </div>
            )}


            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 border-t border-gray-200 bg-white rounded-b-xl">
            {/* Advisory, never blocking: the input stays enabled at zero and
                below — the account owner chose warn-don't-block. */}
            {creditBalance !== null && creditBalance <= 0 && (
              <p data-credit-warning className="mb-2 rounded-md bg-red-50 border border-red-200 px-2.5 py-1.5 text-[11px] text-red-700">
                {t('chat.outOfCredits', { balance: creditBalance.toLocaleString() })}
              </p>
            )}
            {contextPending && (
              <p data-context-pending role="status" className="mb-2 rounded-md bg-gray-50 border border-gray-200 px-2.5 py-1.5 text-[11px] text-gray-700">
                {t('chat.contextPending')}
              </p>
            )}
            <div className="flex items-center space-x-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={inputPlaceholder}
                // `focus:border-transparent` is gone with the yellow ring: it erased the
                // resting gray-300 edge, so focusing the composer USED to make it less
                // visible than it was at rest, not more.
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink-950 transition-all"
                disabled={isLoading || contextPending}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isLoading || contextPending}
                className={`p-2.5 rounded-lg transition-all ${
                  inputValue.trim() && !isLoading && !contextPending
                    ? 'bg-primary-600 hover:bg-primary-700 text-ink-950 shadow-md'
                    : 'bg-gray-200 text-gray-600 cursor-not-allowed'
                }`}
                title={t('chat.send')}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-gray-600 mt-1.5 text-center">
              {t('chat.disclaimer')}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default MediaChatBubble;
